import { useMemo, useState } from 'react';

/*
 * one shared client-side sort for every module table (feeds shared/data_table's sort/on_sort
 * props).
 */
const BLANK = (v) => v === null || v === undefined || v === '' || v === '—';

export const compare_text = (a, b) =>
  String(a).localeCompare(String(b), undefined, { sensitivity: 'base' });

export const compare_number = (a, b) => Number(a) - Number(b);

export const compare_date = (a, b) => {
  const ta = a instanceof Date ? a.getTime() : new Date(a).getTime();
  const tb = b instanceof Date ? b.getTime() : new Date(b).getTime();
  return ta - tb;
};

const COMPARATORS = { text: compare_text, number: compare_number, date: compare_date };

const column_for = (columns, key) => columns.find((c) => c.sort_key === key);

// Decorate-sort-undecorate keeps ties in their original order regardless of asc/desc (a stable sort).
// Blanks always land last, in either direction, so only the two non-blank values go through `dir`.
export const sort_rows = (rows, columns, sort) => {
  if (!sort || !sort.key) return rows;
  const column = column_for(columns, sort.key);
  const sort_type = (column && column.sort_type) || 'text';
  const value = column && column.sort_value ? column.sort_value : (row) => row[sort.key];
  const dir = sort.dir === 'desc' ? -1 : 1;
  const comparator = COMPARATORS[sort_type] || COMPARATORS.text;
  return rows
    .map((row, index) => ({ row, index }))
    .sort((x, y) => {
      const a = value(x.row);
      const b = value(y.row);
      const blank_a = BLANK(a);
      const blank_b = BLANK(b);
      let cmp;
      if (blank_a && blank_b) cmp = 0;
      else if (blank_a) cmp = 1;
      else if (blank_b) cmp = -1;
      else cmp = comparator(a, b) * dir;
      return cmp !== 0 ? cmp : x.index - y.index;
    })
    .map((x) => x.row);
};

// New column starts ascending; the active column toggles asc <-> desc (no third "unsorted" click).
export const toggle_sort = (sort, key) => {
  if (!sort || sort.key !== key) return { key, dir: 'asc' };
  return { key, dir: sort.dir === 'asc' ? 'desc' : 'asc' };
};

export const useSortable = (rows, columns, initial_sort = null) => {
  const [sort, setSort] = useState(initial_sort);
  const on_sort = (key) => setSort((s) => toggle_sort(s, key));
  const sorted_rows = useMemo(() => sort_rows(rows, columns, sort), [rows, columns, sort]);
  return { sort, on_sort, sorted_rows };
};
